import ROUTES from "router/routes";

import {
  buildRouteFromPath,
  normalizePathKey,
  routeToMetadataKey,
} from "./sidebarPath";

export type SidebarDepth = 1 | 2 | 3;

export type ResolvedSidebarItem = {
  key: string;
  route: string;
  label: string;
  icon: string;
  isPremium: boolean;
  isUnmapped: boolean;
};

const DEFAULT_ICON = "/sidebar/default.svg";
const FALLBACK_ICON = "/sidebar/active.svg";

export const PREMIUM_KEYS = new Set([
  "analytic_payments_withdrawals",
  "analytic_global_users",
  "analytic_traffic_affiliates",
  "analytic_traffic_cohorts",
  "analytic_retention_players",
  "analytic_retention_bonuses",
  "analytic_retention_cohorts",
  "analytic_notifications_push",
  "analytic_notifications_email",
  "analytic_notifications_sms",
  "analytic_risk",
  "analytic_support",
  "roles_permissions",
  "roles_modules",
  "live_ops_bonuses_wheels_of_fortune",
  "live_ops_bonuses_loot_boxes",
  "live_ops_missions",
  "live_ops_store",
  "live_ops_jackpots",
  "notifications_sms",
]);

const KNOWN_ROUTES_BY_KEY: Record<string, string> = {};
Object.values(ROUTES).forEach((route) => {
  if (typeof route === "string" && route.startsWith("/") && route !== ROUTES.LOGIN) {
    KNOWN_ROUTES_BY_KEY[routeToMetadataKey(route)] = route;
  }
});

const GATEWAY_ROUTE_ALIASES: Record<string, string> = {
  analytic_sport_competitions: ROUTES.ANALYTIC_SPORTS_COMPETITIONS,
  analytic_sport_categories: ROUTES.ANALYTIC_SPORTS_CATEGORIES,
  game_center_provider: ROUTES.GAME_CENTER_PROVIDERS_LIST,
};

const LEVEL1_ICONS: Record<string, string> = {
  "game center": "/sidebar/gameCenter.svg",
  cms: "/sidebar/gear.svg",
  players: "/sidebar/player.svg",
  "live ops": "/sidebar/liveops.svg",
  notifications: "/sidebar/notifications.svg",
  payments: "/sidebar/payments.svg",
  domains: "/sidebar/domains.svg",
  logs: "/sidebar/logs.svg",
  risk: "/sidebar/risk.svg",
  scheduler: "/sidebar/scheduler.svg",
  support: "/sidebar/support.svg",
  roles: "/sidebar/roles.svg",
  analytic: "/sidebar/analytics.svg",
};

const LEVEL2_ICONS: Record<string, string> = {
  traffic: "/sidebar/traffic.svg",
  global: "/sidebar/global.svg",
  casino: "/sidebar/casino.svg",
  sports: "/sidebar/sport.svg",
  sport: "/sidebar/sport.svg",
  retention: "/sidebar/retention.svg",
  payments: "/sidebar/payments.svg",
  notifications: "/sidebar/notifications.svg",
  games: "/sidebar/games.svg",
  risk: "/sidebar/risk.svg",
  support: "/sidebar/support.svg",
  threads: "/sidebar/threads.svg",
  channels: "/sidebar/channels.svg",
  provider: "/sidebar/provider.svg",
  categories: "/sidebar/category.svg",
  collections: "/sidebar/collection.svg",
  "multi scope": "/sidebar/multi-scope.svg",
  tags: "/sidebar/tags.svg",
  parsing: "/sidebar/parsing.svg",
  widgets: "/sidebar/widgets.svg",
  banners: "/sidebar/banners.svg",
  pages: "/sidebar/pages.svg",
  promo: "/sidebar/promo.svg",
  footer: "/sidebar/footer.svg",
  list: "/sidebar/list.svg",
  segments: "/sidebar/segments.svg",
  bonuses: "/sidebar/bonuses.svg",
  "loyalty program": "/sidebar/loyaltyProgram.svg",
  cashbacks: "/sidebar/cashbacks.svg",
  promocodes: "/sidebar/promocodes.svg",
  "wager games": "/sidebar/games.svg",
  "cashback games": "/sidebar/games.svg",
  tournaments: "/sidebar/tournaments.svg",
  missions: "/sidebar/missions.svg",
  store: "/sidebar/store.svg",
  jackpots: "/sidebar/jackpots.svg",
  push: "/sidebar/push.svg",
  email: "/sidebar/email.svg",
  sms: "/sidebar/sms.svg",
  psp: "/sidebar/gear.svg",
  transactions: "/sidebar/transactions.svg",
  currencies: "/sidebar/currencies.svg",
  zones: "/sidebar/zones.svg",
  mirrors: "/sidebar/mirrors.svg",
  countries: "/sidebar/countries.svg",
  errors: "/sidebar/errors.svg",
  warnings: "/sidebar/warnings.svg",
  notices: "/sidebar/notices.svg",
  permissions: "/sidebar/permissions.svg",
  limits: "/sidebar/limit.svg",
  modules: "/sidebar/modules.svg",
  players: "/sidebar/player.svg",
  kyc: "/sidebar/kyc.svg",
  "kyc documents": "/sidebar/document.svg",
  customized: "/sidebar/customized.svg",
  presets: "/sidebar/presets.svg",
};

const LEVEL3_ICONS: Record<string, string> = {
  ...LEVEL2_ICONS,
  documents: "/sidebar/document.svg",
  rules: "/sidebar/rules.svg",
  "brand core": "/sidebar/brandCore.svg",
  partners: "/sidebar/partners.svg",
  "main text": "/sidebar/mainText.svg",
  applications: "/sidebar/applications.svg",
  "social networks": "/sidebar/socialNetworks.svg",
  seo: "/sidebar/seo.svg",
  faq: "/sidebar/faq.svg",
  "loyalty program": "/sidebar/loyalty-program.svg",
  "wager games": "/sidebar/wager-games.svg",
  "cashback games": "/sidebar/cashback-games.svg",
  providers: "/sidebar/provider.svg",
  users: "/sidebar/users.svg",
  common: "/sidebar/common.svg",
  competitions: "/sidebar/competitions.svg",
  affiliates: "/sidebar/affiliates.svg",
  cohorts: "/sidebar/cohorts.svg",
  methods: "/sidebar/methods.svg",
  operations: "/sidebar/operations.svg",
  deposits: "/sidebar/deposits.svg",
  withdrawals: "/sidebar/withdrawals.svg",
  compensation: "/sidebar/compensation.svg",
  compensations: "/sidebar/compensation.svg",
  confiscation: "/sidebar/confiscation.svg",
  confiscations: "/sidebar/confiscation.svg",
  new: "/sidebar/new.svg",
  active: "/sidebar/active.svg",
  hidden: "/sidebar/hidden.svg",
  deactivated: "/sidebar/deactivated.svg",
  "no category": "/sidebar/category.svg",
  "main page": "/sidebar/pages.svg",
  "burger menu": "/sidebar/burger.svg",
  "game bar": "/sidebar/gameBar.svg",
  "sign up form": "/sidebar/signUp.svg",
  "pre deposit form": "/sidebar/preDepositForm.svg",
  "profile menu": "/sidebar/profileMenu.svg",
  "mobile menu": "/sidebar/mobileMenu.svg",
  manual: "/sidebar/manual.svg",
  auto: "/sidebar/auto.svg",
  "free spins": "/sidebar/free-spins.svg",
  "wheels of fortune": "/sidebar/wheel.svg",
  "loot boxes": "/sidebar/modules.svg",
  levels: "/sidebar/levels.svg",
  "sign up points": "/sidebar/signUp.svg",
  "deposit points": "/sidebar/deposits.svg",
  "deposit points limits": "/sidebar/deposits.svg",
  "bets points": "/sidebar/bets.svg",
  "not processing": "/sidebar/processing.svg",
  header: "/sidebar/header.svg",
  "wager points": "/sidebar/wager.svg",
  "casino page": "/sidebar/casino.svg",
  "live casino": "/sidebar/casino.svg",
};

export function getSidebarIcon(segment: string, depth: SidebarDepth): string {
  const key = segment.trim().toLowerCase();

  if (depth === 1) {
    return LEVEL1_ICONS[key] ?? FALLBACK_ICON;
  }

  if (depth === 2) {
    return LEVEL2_ICONS[key] ?? FALLBACK_ICON;
  }

  return LEVEL3_ICONS[key] ?? DEFAULT_ICON;
}
export function resolveSidebarItem(pathSegments: string[]): ResolvedSidebarItem {
  const key = normalizePathKey(...pathSegments);
  const knownRoute =
    GATEWAY_ROUTE_ALIASES[key] ?? KNOWN_ROUTES_BY_KEY[key] ?? null;
  const route = knownRoute ?? buildRouteFromPath(pathSegments);
  const depth = Math.min(pathSegments.length, 3) as SidebarDepth;
  const label = pathSegments[pathSegments.length - 1] ?? "";

  return {
    key,
    route,
    label,
    icon: getSidebarIcon(label, depth),
    isPremium: PREMIUM_KEYS.has(key),
    isUnmapped: knownRoute === null,
  };
}
